// A chooser only: no draw state, storage, commerce, or tool initialization.
export function enhanceSpreadGuide(root, win = window) {
  if (!root || root.hasAttribute('data-enhanced')) return null;
  const options = [...root.querySelectorAll('[data-spread]')];
  const library = root.querySelector('[data-position-library]');
  const dialog = root.querySelector('[data-position-dialog]');
  if (!options.length || !library || typeof dialog?.showModal !== 'function') return null;
  const mount = dialog.querySelector('[data-detail-mount]');
  const closeControl = dialog.querySelector('[data-close-positions]');
  const groups = new Map([...library.querySelectorAll('[data-detail]')].map(el => [el.dataset.detail, el]));
  let selected = 'three', active = null, opener = null, savedOverflow = '', closing = null, epoch = 0;
  const getOption = key => options.find(o => o.dataset.spread === key);
  const setHash = (hash, mode = 'push') => {
    if (win.location.hash === hash) return;
    const url = new URL(win.location.href); url.hash = hash;
    win.history[mode === 'replace' ? 'replaceState' : 'pushState'](win.history.state, '', url);
  };
  function select(key, history = false) {
    const option = getOption(key); if (!option) return;
    selected = key;
    options.forEach(o => {
      o.querySelector('input').checked = o === option;
      o.toggleAttribute('data-selected', o === option);
    });
    root.querySelector('[data-selected-count]').textContent = option.dataset.count + ' cards';
    root.querySelector('[data-practice-label]').textContent = 'Practise ' + option.dataset.count + ' cards free';
    root.querySelector('[data-selected-practice]').href = option.dataset.practiceUrl;
    if (history) setHash('#spread-' + key);
  }
  function showPosition(group, number) {
    group.querySelectorAll('[data-position-note]').forEach(el => { el.open = el.dataset.positionNote === String(number); });
  }
  function restoreGroup() {
    if (active) library.append(groups.get(active));
    active = null;
  }
  function finishClose(restoreFocus) {
    if (dialog.open) dialog.close();
    restoreGroup();
    root.ownerDocument.documentElement.style.overflow = savedOverflow;
    if (restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
    opener = null;
  }
  function close({ history = true, restoreFocus = true, animate = true } = {}) {
    if (!dialog.open) return;
    if (history) setHash('#spread-' + selected, 'replace');
    const token = ++epoch;
    closing?.cancel(); closing = null;
    if (animate && !win.matchMedia('(prefers-reduced-motion: reduce)').matches && dialog.animate) {
      closing = dialog.animate([{ transform:'translateX(0)' }, { transform:'translateX(100%)' }], { duration:200, easing:'ease-out', fill:'forwards' });
      closing.finished.then(() => { if (epoch === token) { finishClose(restoreFocus); closing.cancel(); closing = null; } }).catch(() => {});
    } else finishClose(restoreFocus);
  }
  function open(key, position = 1, { history = true, trigger = null } = {}) {
    const group = groups.get(key), option = getOption(key);
    if (!group || !option || position < 1 || position > Number(option.dataset.count)) return;
    ++epoch; closing?.cancel(); closing = null;
    select(key);
    if (!dialog.open) {
      opener = trigger || root.ownerDocument.activeElement;
      savedOverflow = root.ownerDocument.documentElement.style.overflow;
      root.ownerDocument.documentElement.style.overflow = 'hidden';
    }
    restoreGroup(); active = key; mount.append(group); showPosition(group, position);
    dialog.setAttribute('aria-labelledby', 'SpreadDetail-' + key);
    if (!dialog.open) dialog.showModal();
    if (history) setHash('#' + key + '-position-' + position);
  }
  function fromUrl() {
    const match = win.location.hash.match(/^#(?:(?:spread-(three|five|seven))|(?:(three|five|seven)-position-([1-7])))$/);
    const key = match?.[1] || match?.[2] || 'three';
    const position = Number(match?.[3]);
    if (match?.[2] && position <= Number(getOption(key).dataset.count)) {
      open(key, position, { history:false });
    } else {
      close({ history:false, animate:false, restoreFocus:false }); select(key);
    }
  }
  root.addEventListener('change', e => {
    const input = e.target.closest('input[data-choose-spread]');
    if (input) select(input.value, true);
  });
  root.addEventListener('click', e => {
    const detailLink = e.target.closest('[data-view-positions]');
    if (detailLink) { e.preventDefault(); open(detailLink.dataset.viewPositions, 1, { trigger:detailLink }); return; }
    const summary = e.target.closest('[data-position]');
    if (!summary || !dialog.open) return;
    e.preventDefault();
    const current = summary.closest('details'), wasOpen = current.open;
    const group = groups.get(active);
    group.querySelectorAll('[data-position-note]').forEach(el => { el.open = el === current && !wasOpen; });
    setHash(wasOpen ? '#' + active + '-position-1' : '#' + active + '-position-' + summary.dataset.position, 'replace');
  });
  closeControl.addEventListener('click', () => close());
  dialog.addEventListener('cancel', e => { e.preventDefault(); close(); });
  dialog.addEventListener('click', e => {
    if (e.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
  });
  // Explicit cycling also covers browsers that briefly focus BODY at a modal boundary.
  dialog.addEventListener('keydown', e => {
    if (e.key !== 'Tab' || !dialog.open) return;
    const stops = [...dialog.querySelectorAll('button:not([disabled]),a[href],summary,[tabindex]')]
      .filter(el => el.tabIndex >= 0 && el.getClientRects().length && !el.closest('[hidden]'));
    const first = stops[0] || closeControl, last = stops.at(-1) || closeControl;
    const focused = root.ownerDocument.activeElement;
    if (!dialog.contains(focused) || (e.shiftKey ? focused === first : focused === last)) {
      e.preventDefault(); (e.shiftKey ? last : first).focus({ preventScroll:true });
    }
  });
  root.ownerDocument.addEventListener('focusin', e => {
    if (dialog.open && !dialog.contains(e.target)) closeControl.focus({ preventScroll:true });
  });
  win.addEventListener('popstate', fromUrl);
  win.addEventListener('hashchange', fromUrl);
  root.setAttribute('data-enhanced', '');
  fromUrl();
  return { select, open, close, fromUrl, get selected() { return selected; } };
}
if (typeof document !== 'undefined') document.querySelectorAll('[data-spread-guide]').forEach(root => enhanceSpreadGuide(root));
